import { __rest } from "tslib";
import { Slot, Slottable } from '@radix-ui/react-slot';
import React, { forwardRef } from 'react';
import { tv } from 'tailwind-variants';
import { ChildrenWithIcon, } from './ChildrenWithIcon';
const button = tv({
    base: [
        // Basic reset
        'appearance-none',
        'bg-transparent',
        'border-none',
        'cursor-pointer',
        'ring-1',
        'ring-transparent',
        'select-none',
        // Layout
        'py-0',
        'flex',
        'box-border',
        'items-center',
        'justify-center',
        // Typography
        'font-body',
        'font-semibold',
        'text-center',
        'no-underline',
        'tracking-normal',
        // Transition
        'duration-300',
        'ease-smooth',
        'transition-combined',
        'will-change-transform',
        // Repeated active styles
        'active:translate-y-px',
        'active:outline-transparent',
        // Repeated focus styles
        'focus-visible:outline-none',
        'focus-visible:outline-gray-300',
        // It's easier and more maintainable to replicate the disabled state across
        // all variants, than to try and override create a custom one for each variant
        'disabled:bg-gray-200',
        'disabled:text-gray-500',
        'disabled:ring-transparent',
        'disabled:shadow-none',
        'disabled:cursor-not-allowed',
        'disabled:hover:translate-y-0',
        'disabled:active:translate-y-0',
    ],
    variants: {
        variant: {
            primary: [
                'bg-gray-900',
                'text-gray-50',
                'ring-gray-900',
                // Hover state
                'hover:shadow-md',
                'hover:bg-gray-950',
                'hover:ring-gray-950',
                'hover:-translate-y-px',
                // Active state
                'active:shadow-none',
            ],
            secondary: [
                'bg-white-900',
                'text-gray-900',
                'ring-gray-600/20',
                'shadow-sm-longer',
                // Hover state
                'hover:bg-gray-50',
                'hover:shadow-md',
                'hover:ring-gray-600/40',
                'hover:-translate-y-px',
                // Focus state
                'focus-visible:ring-gray-600',
                // Active state
                'active:bg-gray-100',
                'active:ring-gray-600/25',
                'active:shadow-sm-longer',
            ],
            ghost: [
                'bg-transparent',
                'text-gray-900',
                // Hover state
                'hover:bg-gray-400/25',
                'hover:ring-gray-400/25',
                'active:bg-gray-400/50',
                'active:ring-transparent',
                // Focus state
                'focus-visible:ring-gray-600',
            ],
            danger: [
                'bg-red-600',
                'text-white',
                'ring-red-600',
                // Hover state
                'hover:shadow-md',
                'hover:bg-red-700',
                'hover:ring-red-700',
                'hover:-translate-y-px',
                // Active state
                'active:shadow-none',
            ],
            legacyBlue: [
                'bg-legacyBlue',
                'text-gray-50',
                'ring-legacyBlue',
                // Hover state
                'hover:shadow-md',
                'hover:bg-legacyBlueHover',
                'hover:ring-legacyBlueHover',
                'hover:-translate-y-px',
                // Active state
                'active:shadow-none',
            ],
        },
        size: {
            sm: [
                'h-7',
                'leading-7',
                'pl-3',
                'pr-3',
                'gap-1.5',
                'text-xs',
                'rounded-md',
                // Tailwind doesn't work with dynamically generated classes so we'll
                // have to hardcode the class name here. It is defined in ./ChildrenWithIcon
                '[&>.inner-icon>svg]:h-3.5',
                '[&>.inner-icon>svg]:w-3.5',
            ],
            base: [
                'h-9',
                'leading-9',
                'pl-4',
                'pr-4',
                'gap-2',
                'text-sm',
                'rounded-md',
                // Tailwind doesn't work with dynamically generated classes so we'll
                // have to hardcode the class name here. It is defined in ./ChildrenWithIcon
                '[&>.inner-icon>svg]:h-4',
                '[&>.inner-icon>svg]:w-4',
            ],
        },
        width: {
            full: ['w-full'],
        },
        leadingIcon: {
            true: [],
            false: [],
        },
        trailingIcon: {
            true: [],
            false: [],
        },
        standaloneIcon: {
            true: ['pl-0', 'pr-0'],
            false: [],
        },
    },
    compoundVariants: [
        // Standalone icon
        {
            className: 'w-7',
            size: 'sm',
            standaloneIcon: true,
        },
        {
            className: 'w-9',
            size: 'base',
            standaloneIcon: true,
        },
        // Leading icon
        {
            className: 'pl-2.5',
            size: 'sm',
            leadingIcon: true,
        },
        {
            className: 'pl-3.5',
            size: 'base',
            leadingIcon: true,
        },
        // Trailing icon
        {
            className: 'pr-2.5',
            size: 'sm',
            trailingIcon: true,
        },
        {
            className: 'pr-3.5',
            size: 'base',
            trailingIcon: true,
        },
    ],
    defaultVariants: {
        variant: 'secondary',
        size: 'base',
    },
});
/**
  All purpose button component
  */
const BtwButton = forwardRef(function Button(props, ref) {
    const { asChild, children, className, disabled, isLoading, leadingIcon, size, standaloneIcon, testId, trailingIcon, type = 'button', variant, width } = props, rest = __rest(props, ["asChild", "children", "className", "disabled", "isLoading", "leadingIcon", "size", "standaloneIcon", "testId", "trailingIcon", "type", "variant", "width"]);
    const Comp = asChild ? Slot : 'button';
    return (React.createElement(Comp, Object.assign({ className: button({
            className,
            size,
            variant,
            width,
            standaloneIcon: !!standaloneIcon,
            leadingIcon: (!!leadingIcon || isLoading) && !standaloneIcon,
            trailingIcon: !!trailingIcon && !isLoading && !standaloneIcon,
        }), "data-testid": testId, disabled: disabled || isLoading, ref: ref, type: type }, rest),
        isLoading ? (React.createElement(ChildrenWithIcon.Loader, null)) : (React.createElement(React.Fragment, null, (standaloneIcon || leadingIcon) && (React.createElement(ChildrenWithIcon.Icon, null, standaloneIcon || leadingIcon)))),
        React.createElement(Slottable, null, children),
        !isLoading && !standaloneIcon && trailingIcon && (React.createElement(ChildrenWithIcon.Icon, null, trailingIcon))));
});
export { BtwButton };
